import { useEffect, useRef, useState, useCallback } from 'react'

import './UnifiedPopup.css'

// Components
import { SearchFilters } from './SearchFilters'

// Auth
import { useAuth } from '../auth/AuthContext'

// Types
import type { 
  Progression, 
  GuitarArrangement,
  AccessLevel
} from '../presets/presetSchema'

// API
import { 
  getProgressionChords,
  searchProgressions,
  detectFiltersFromQuery,
  type SearchOptions
} from '../presets/presetAPI'

/**
 * Unified Popup Component
 * Container for all popup sections that appear above the unified text box
 */

// Interface for unified search results
interface UnifiedSearchResults {
  progressions: Progression[];
  totalCount: number;
  appliedFilters: {
    genres?: any[];
    difficulties?: any[];
    keys?: string[];
    guitarCounts?: string[];
    accessLevels?: AccessLevel[];
    features?: any[];
    sources?: any[];
  };
}

// Interface for component props
interface UnifiedPopupProps {
  isVisible: boolean;
  onClose: () => void;
  input: string;
  onGenerate?: (value: string) => void; // Optional since we don't use it anymore
  onReplaceFromArrangements?: (arrangements: GuitarArrangement[], progressionMetadata?: { accessLevel?: string }) => void;
  className?: string;
}

export function UnifiedPopup({ 
  isVisible, 
  onClose, 
  input,
  onReplaceFromArrangements,
  className = ""
}: UnifiedPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Get user auth state for tier-based filtering
  const { user } = useAuth();
  
  // State for unified search results and filters
  const [searchResults, setSearchResults] = useState<UnifiedSearchResults>({ 
    progressions: [], 
    totalCount: 0, 
    appliedFilters: {} 
  });
  const [dataSource, setDataSource] = useState<{ fromCache: boolean; userFromCache: boolean }>({ fromCache: false, userFromCache: false });
  const [loadingProgressions, setLoadingProgressions] = useState<boolean>(false);

  // Enhanced search state - Simplified
  const [manualFilters, setManualFilters] = useState<SearchOptions['filters']>({});
  const [detectedFilters, setDetectedFilters] = useState<SearchOptions['filters']>({});

  // Helper function to render access level badge
  const renderAccessLevelBadge = useCallback((accessLevel: AccessLevel | undefined) => {
    // Only show badges for non-premium users (free users and unauthenticated users)
    if (user?.tier === 'premium' || !accessLevel) {
      return null;
    }

    if (accessLevel === 'free') {
      return <span className="access-level-badge free">FREE</span>;
    } else if (accessLevel === 'premium_preview') {
      return <span className="access-level-badge preview">PREVIEW</span>;
    }
    
    return null;
  }, [user]);

  // Load progressions and perform search when component mounts or input changes
  useEffect(() => {
    const performSearch = async () => {
      if (!isVisible) return;
      
      setLoadingProgressions(true);
      try {
        // Detect filters from query text
        const queryDetectedFilters = input.trim() ? detectFiltersFromQuery(input.trim()) : {};
        setDetectedFilters(queryDetectedFilters);
        
        // Combine detected filters with manual filters (manual filters override detected)
        const combinedFilters = { ...queryDetectedFilters };
        
        // Apply manual filters, overriding detected ones
        if (manualFilters?.genres?.length) combinedFilters.genres = manualFilters.genres;
        if (manualFilters?.difficulties?.length) combinedFilters.difficulties = manualFilters.difficulties;
        if (manualFilters?.keys?.length) combinedFilters.keys = manualFilters.keys;
        if (manualFilters?.guitarCounts?.length) combinedFilters.guitarCounts = manualFilters.guitarCounts;
        if (manualFilters?.sources?.length) combinedFilters.sources = manualFilters.sources;
        if (manualFilters?.favorites !== undefined) combinedFilters.favorites = manualFilters.favorites;
        
        // Apply tier-based access level filtering for non-premium users
        if (user?.tier !== 'premium') {
          combinedFilters.accessLevels = ['free', 'premium_preview'];
        }
        
        // Build search options
        const searchOptions: SearchOptions = {
          query: input.trim() || undefined, // undefined for empty search (shows popular)
          filters: combinedFilters,
          sortBy: 'relevance',
          limit: 50 // Reasonable limit for UI performance
        };
        
        // Perform unified search
        const result = await searchProgressions(searchOptions);
        
        if (result.success && result.data) {
          setSearchResults({
            progressions: result.data,
            totalCount: result.data.length,
            appliedFilters: combinedFilters
          });
          
          // Mark as cached since search uses cached data
          setDataSource({ fromCache: true, userFromCache: true });
        }
      } catch {
        // Silently handle search errors
        setSearchResults({ progressions: [], totalCount: 0, appliedFilters: {} });
      } finally {
        setLoadingProgressions(false);
      }
    };

    performSearch();
  }, [isVisible, input, user?.tier, manualFilters]);

  // Modal opens at top (no auto-scroll) - filters are immediately visible

  // Handle click outside to close - RE-ENABLED WITH PROPER LOGIC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Only close if click is truly outside the popup content
      if (contentRef.current && !contentRef.current.contains(target)) {
        // Also check if click is not on the text input (allow typing)
        if (!target.closest('.unified-text-box')) {
          onClose();
        }
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Handle clicking on a progression to replace all guitars
  const handleProgressionClick = useCallback((progression: Progression) => {
    // All progressions now have arrangement data
    if (progression.arrangements && progression.arrangements.length > 0 && onReplaceFromArrangements) {
      // Pass progression metadata to detect premium preview progressions
      const progressionMetadata = {
        accessLevel: progression.metadata?.accessLevel
      };
      onReplaceFromArrangements(progression.arrangements, progressionMetadata);
    }
    
    // Close popup after replacement
    onClose();
  }, [onReplaceFromArrangements, onClose]);



  // Enhanced search handlers - Phase 2A
  const handleFiltersChange = useCallback((newFilters: SearchOptions['filters']) => {
    setManualFilters(newFilters);
  }, []);

  if (!isVisible) return null;

  // Get unified progression data 
  const { progressions: allProgressions } = searchResults;
  
  // Show all progressions in unified display (both user and standard)
  const displayProgressions = allProgressions;

  return (
    <div className={`unified-popup ${className}`} ref={popupRef}>
      <div className="unified-popup-content" ref={contentRef}>
        
        {/* Enhanced Search Filters - Simplified */}
        <SearchFilters
          appliedFilters={manualFilters}
          detectedFilters={detectedFilters}
          onFiltersChange={handleFiltersChange}
        />
        
        {/* Unified Progressions Section */}
        <div className="popup-section unified-progressions-section">
          <h3>
            🎸 Progressions {input.trim() && `(${displayProgressions.length} found)`}
            {dataSource.fromCache && (
              <span className="cache-indicator" title="Loaded from cache">⚡</span>
            )}
          </h3>
          <div className="section-content">
            {loadingProgressions && displayProgressions.length === 0 ? (
              <div className="loading-state">
                <p>Loading progressions...</p>
              </div>
            ) : displayProgressions.length === 0 ? (
              <p className="empty-state-message">No progressions found</p>
            ) : (
              <div className="progressions-grid">
                {displayProgressions.map((progression) => (
                  <div 
                    key={progression.id}
                    className="progression-card"
                    onClick={() => handleProgressionClick(progression)}
                  >
                    <h4>
                      {progression.name}
                      <div className="progression-header-badges">
                        <span className="progression-genre">
                          {progression.metadata?.genre || 'Standard'}
                        </span>
                        {renderAccessLevelBadge(progression.metadata?.accessLevel)}
                      </div>
                    </h4>
                    <div className="progression-chords">
                      {getProgressionChords(progression).join(' ')}
                    </div>
                    <div className="progression-description">
                      {progression.metadata?.description || ''}
                    </div>
                    {progression.metadata?.pattern && (
                      <div className="progression-pattern">
                        {progression.metadata.pattern}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}          </div>
        </div>
      </div>
    </div>
  );
}
